import type { Mark, NoughtsView } from '@cardengine/shared';
import { useStore } from '../store.js';

/**
 * The grid.
 *
 * Drawn rather than typed: an `X` and an `O` in the page font are a letter and
 * a letter, and at board size they read as a word. These are two strokes and a
 * ring, with round caps, which is what someone actually puts on paper — and
 * being SVG they stay crisp at whatever size the grid ends up.
 */
function MarkGlyph({ mark }: { mark: Mark }) {
  return (
    <svg className={`ncell__glyph ncell__glyph--${mark === 'X' ? 'cross' : 'nought'}`} viewBox="0 0 100 100" aria-hidden="true">
      {mark === 'X' ? (
        <>
          <line x1="24" y1="24" x2="76" y2="76" />
          <line x1="76" y1="24" x2="24" y2="76" />
        </>
      ) : (
        <circle cx="50" cy="50" r="27" />
      )}
    </svg>
  );
}

/**
 * The stroke through a winning line.
 *
 * Positioned from the two end squares rather than from a lookup of every
 * possible line, so it works unchanged on a 3×3, a 4×4 or a five-long run that
 * only needed four. The viewBox is the board's own square, and
 * `preserveAspectRatio="none"` lets it stretch with the grid.
 */
function WinStroke({ line, boardSize }: { line: number[]; boardSize: number }) {
  const first = line[0];
  const last = line[line.length - 1];
  if (first === undefined || last === undefined) return null;

  const cellSize = 100 / boardSize;
  const centre = (cell: number) => ({
    x: ((cell % boardSize) + 0.5) * cellSize,
    y: (Math.floor(cell / boardSize) + 0.5) * cellSize,
  });
  const from = centre(first);
  const to = centre(last);

  // Carried past both end squares rather than stopping at their centres. A line
  // that ends in the middle of the last mark looks like it ran out; the one you
  // would actually draw goes through and out the other side.
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = Math.hypot(dx, dy) || 1;
  const overshoot = cellSize * 0.32;
  const ox = (dx / length) * overshoot;
  const oy = (dy / length) * overshoot;

  return (
    <svg className="nboard__stroke" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
      <line x1={from.x - ox} y1={from.y - oy} x2={to.x + ox} y2={to.y + oy} />
    </svg>
  );
}

/** "Row 2, column 3" — how a square gets announced. */
function squareName(cell: number, boardSize: number): string {
  return `Row ${Math.floor(cell / boardSize) + 1}, column ${(cell % boardSize) + 1}`;
}

export function NoughtsBoard({ view }: { view: NoughtsView }) {
  const mark = useStore((s) => s.noughtsMark);
  const rejection = useStore((s) => s.rejection);

  const yourTurn = view.currentPlayerId === view.yourPlayerId && view.phase === 'playing';
  const playable = new Set(view.yourLegalMoves.map((move) => move.cell));
  const winning = new Set(view.winningLine ?? []);

  return (
    <div className="nboard-wrap">
      <div
        className={`nboard nboard--${view.boardSize} ${yourTurn ? 'is-yours' : ''}`}
        style={{ '--nboard-size': view.boardSize } as React.CSSProperties}
        role="group"
        aria-label={`${view.boardSize} by ${view.boardSize} grid`}
      >
        {view.board.map((square, cell) => {
          const open = square === null;
          const canPlay = yourTurn && playable.has(cell);

          return (
            <button
              key={cell}
              type="button"
              className={`ncell ${open ? '' : 'ncell--taken'} ${winning.has(cell) ? 'ncell--won' : ''} ${
                canPlay ? 'ncell--open' : ''
              }`}
              // Squares stay in the tab order once taken so the board can still
              // be read through with a keyboard; they simply cannot be pressed.
              disabled={!canPlay}
              onClick={() => mark(cell)}
              aria-label={
                square
                  ? `${squareName(cell, view.boardSize)}, ${square === 'X' ? 'cross' : 'nought'}`
                  : `${squareName(cell, view.boardSize)}, empty`
              }
            >
              {square && <MarkGlyph mark={square} />}
              {/* What your mark would look like there. Hover and focus only, so
                  it never competes with what is actually on the board. */}
              {open && canPlay && view.yourMark && (
                <span className="ncell__ghost" aria-hidden="true">
                  <MarkGlyph mark={view.yourMark} />
                </span>
              )}
            </button>
          );
        })}

        {view.winningLine && <WinStroke line={view.winningLine} boardSize={view.boardSize} />}
      </div>

      {/* Sits under the grid rather than floating over it, for the reason the
          Switch table gives: feedback about your own move belongs beside the
          move, not on top of the opponent. */}
      <p className={`nboard__rejection ${rejection ? 'is-shown' : ''}`} role="status">
        {rejection?.text ?? ' '}
      </p>
    </div>
  );
}
